import { useRouter } from 'next/router';
import { useMemo } from 'react';

import {
  getLineupEntryId,
  getLineupSettings,
  getLineupType,
  getLineupViews,
  parseLineupPath,
} from 'utils/lineup.utils';

import { LINEUP_ENTRY_PARAM, LineupType } from 'constants/lineup.constants';
import { useLineups } from 'queries/lineup/useLineups';
import {
  ImageFileFragment,
  LineupStandardFragment,
  LineupXpFragment,
} from 'types/api/dato-operations.types';

import { Timed } from 'components/@lineup/utils';

/**
 * Read off the pathname rather than threaded through props, so the layout stays
 * independent of the page it wraps.
 */
export const useLineupRoute = () => {
  const { query, pathname } = useRouter();
  const { programs, lineups, isLoading } = useLineups();

  const { kind, view } = parseLineupPath(pathname);

  let type: LineupType | undefined;

  // Guessing before the lineups land puts the standard tabs on an XP event,
  // then swaps them under the reader.
  if (!isLoading) {
    type = getLineupType({
      hasXp: !!programs?.length,
      hasStandard: !!lineups?.length,
    });
  }

  // The header wants a cover and the times, which the two lineups hold under
  // different names: XP counts sessions, standard sets.
  let cover: ImageFileFragment | undefined;
  let slots: Array<Timed> = [];
  let showing: LineupXpFragment | LineupStandardFragment | undefined;

  if (type === 'xp') {
    // The kind in the path picks which programme is showing.
    const program = programs?.find((item) => item.kind === kind);

    cover = program?.cover;
    slots = program?.sessions ?? [];
    showing = program;
  }

  if (type === 'standard') {
    const [lineup] = lineups ?? [];

    cover = lineup?.cover;
    slots = lineup?.entries ?? [];
    showing = lineup;
  }

  const settings = useMemo(() => getLineupSettings(showing), [showing]);

  return {
    slug: `${query.slug}`,
    isLoading,
    type,
    kind,
    view,
    cover,
    slots,
    settings,
    views: type ? getLineupViews(type, kind, settings.hiddenSections) : [],
    entryId: getLineupEntryId(query[LINEUP_ENTRY_PARAM]),
  };
};
